import { ChannelHistogramPreview, HistogramFlavorPreview } from 'api/slideMultiplexChannelNormalizations';
import { PlotlyFigure } from 'interfaces/agentSession';
import { MarkerNormalizationObservation, SlideNormalizationSummary } from './normalizationInsights';

export interface MarkerNormalizationFigureSet {
  rangeFigure: PlotlyFigure | null;
  zScoreFigure: PlotlyFigure | null;
}

const zScoreLimitPadding = 0.5;
const eightBitHistogramBinCount = 2 ** 8;
const sixteenBitHistogramBinCount = 2 ** 16;
const histogramLowerMassCutoff = 0;
const histogramUpperMassCutoff = 0.999;
const histogramColorPalette = [
  'rgba(2, 136, 209, 0.95)',
  'rgba(239, 108, 0, 0.96)',
  'rgba(94, 53, 177, 0.92)',
  'rgba(0, 137, 123, 0.92)',
];
const histogramFlavorSortOrder = ['raw', 'nsm', 'median_filtered', 'median_filter'];
const histogramFlavorColorByName: Record<string, string> = {
  raw: histogramColorPalette[0],
  overall: histogramColorPalette[0],
  nsm: histogramColorPalette[1],
  median_filtered: histogramColorPalette[2],
  median_filter: histogramColorPalette[2],
};

interface HistogramPreviewBin {
  binStart: number;
  binEnd: number;
  binCenter: number;
  binLabel: string;
  binWidth: number;
  count: number;
  normalizedWeight: number;
}

type HistogramTraceCustomData = [string, number, number, number, number, string];

const pixelCountFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});
const compactPixelCountFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
});

const formatPixelCount = (count: number) => {
  if (!Number.isFinite(count)) {
    return '0';
  }

  const absoluteCount = Math.abs(count);

  if (absoluteCount >= 1_000_000_000) {
    return `${compactPixelCountFormatter.format(count / 1_000_000_000)}B`;
  }

  if (absoluteCount >= 1_000_000) {
    return `${compactPixelCountFormatter.format(count / 1_000_000)}M`;
  }

  if (absoluteCount >= 1_000) {
    return `${compactPixelCountFormatter.format(count / 1_000)}K`;
  }

  return pixelCountFormatter.format(count);
};

const buildPlotThemeModifiers = (chartType: 'box' | 'scatter') => {
  if (chartType === 'box') {
    return {
      axisPadding: { y: 0.1 },
    };
  }

  return {
    axisPadding: { x: 0.08, y: 0.08 },
  };
};

const buildStudySlidePointLink = (customdataIndex: number, markerIdCustomdataIndex?: number) => ({
  type: 'study_slide',
  customdataIndex,
  ...(typeof markerIdCustomdataIndex === 'number' ? { markerIdCustomdataIndex } : {}),
});

const getBubbleSize = (rangeZScore: number) => {
  return Math.min(34, Math.max(10, 10 + Math.abs(rangeZScore) * 5));
};

const getAxisLimit = (maxAbsoluteZScore: number) => {
  return Math.max(2.5, Math.ceil(maxAbsoluteZScore + zScoreLimitPadding));
};

const getHistogramXAxisTitle = (sourceBinCount: number, bitDepth?: number | null) => {
  if (bitDepth === 8 || sourceBinCount === eightBitHistogramBinCount) {
    return 'Signal value (8-bit, 0-255)';
  }

  if (bitDepth === 16 || sourceBinCount === sixteenBitHistogramBinCount) {
    return 'Signal value (16-bit, 0-65535)';
  }

  return `Signal value (0-${Math.max(sourceBinCount - 1, 0)})`;
};

export const getHistogramFlavorLabel = (flavor: string) => {
  const normalizedFlavor = flavor.toLowerCase();

  if (normalizedFlavor === 'raw' || normalizedFlavor === 'overall') {
    return 'Overall';
  }

  if (normalizedFlavor === 'nsm') {
    return 'Filtered to cell segmentations (signal)';
  }

  if (normalizedFlavor === 'median_filtered' || normalizedFlavor === 'median_filter') {
    return 'Median filter';
  }

  return flavor
    .split('_')
    .map((part) => {
      if (!part) {
        return '';
      }

      if (part.toLowerCase() === 'nsm') {
        return 'Cell segmentation signal';
      }

      return `${part[0].toUpperCase()}${part.slice(1)}`;
    })
    .join(' ');
};

export const getHistogramFlavorColor = (flavor: string, fallbackIndex = 0) =>
  histogramFlavorColorByName[flavor.toLowerCase()] ||
  histogramColorPalette[fallbackIndex % histogramColorPalette.length];

export const getHistogramFlavorEntries = (histogramPreview: ChannelHistogramPreview) => {
  const explicitFlavorEntries = Object.entries(histogramPreview.flavors || {}).filter(
    (entry): entry is [string, HistogramFlavorPreview] => Boolean(entry[1])
  );

  if (explicitFlavorEntries.length) {
    return explicitFlavorEntries.sort(([leftFlavor], [rightFlavor]) => {
      const leftPriority = histogramFlavorSortOrder.indexOf(leftFlavor);
      const rightPriority = histogramFlavorSortOrder.indexOf(rightFlavor);

      if (leftPriority !== -1 || rightPriority !== -1) {
        if (leftPriority === -1) {
          return 1;
        }

        if (rightPriority === -1) {
          return -1;
        }

        return leftPriority - rightPriority;
      }

      return leftFlavor.localeCompare(rightFlavor);
    });
  }

  return [
    histogramPreview.raw ? (['raw', histogramPreview.raw] as const) : null,
    histogramPreview.nsm ? (['nsm', histogramPreview.nsm] as const) : null,
  ].filter(Boolean) as Array<[string, HistogramFlavorPreview]>;
};

const buildHistogramPreviewBins = (histogramFlavor: HistogramFlavorPreview): HistogramPreviewBin[] => {
  const previewBinCount = Math.max(histogramFlavor.previewBinCount || 0, 1);
  const sourceBinCount = Math.max(histogramFlavor.sourceBinCount || previewBinCount, previewBinCount);
  const nonzeroBinCounts = histogramFlavor.nonzeroBinCounts || [];
  const nonzeroBinWeights = histogramFlavor.nonzeroBinWeights || [];
  const sparseValueCount = Math.max(nonzeroBinCounts.length, nonzeroBinWeights.length);
  const sparseBinCount = Math.min(histogramFlavor.nonzeroBinIndices.length, sparseValueCount);

  return Array.from({ length: sparseBinCount }, (_, sparseIndex) => {
    const binIndex = histogramFlavor.nonzeroBinIndices[sparseIndex];
    const explicitCount = nonzeroBinCounts[sparseIndex];
    const explicitWeight = nonzeroBinWeights[sparseIndex];
    const normalizedWeight =
      explicitWeight ??
      (histogramFlavor.totalCount > 0 && explicitCount !== undefined ? explicitCount / histogramFlavor.totalCount : 0);
    const count = explicitCount ?? normalizedWeight * histogramFlavor.totalCount;
    const binStart = Math.floor((binIndex * sourceBinCount) / previewBinCount);
    const nextBinStart =
      binIndex === previewBinCount - 1
        ? sourceBinCount
        : Math.floor(((binIndex + 1) * sourceBinCount) / previewBinCount);
    const binWidth = Math.max(nextBinStart - binStart, 1);
    const binEnd = binStart + binWidth - 1;

    return {
      binCenter: binStart + (binWidth - 1) / 2,
      binLabel: binStart === binEnd ? `${binStart}` : `${binStart}-${binEnd}`,
      binWidth,
      binStart,
      binEnd,
      count,
      normalizedWeight,
    };
  }).filter((bin) => bin.count > 0 || bin.normalizedWeight > 0);
};

const buildHistogramTracePoints = ({
  bins,
  histogramFlavor,
}: {
  bins: HistogramPreviewBin[];
  histogramFlavor: HistogramFlavorPreview;
}) => {
  const sourceBinCount = Math.max(
    histogramFlavor.sourceBinCount || histogramFlavor.previewBinCount || 0,
    histogramFlavor.previewBinCount || 0,
    1
  );
  const xValues: number[] = [];
  const yValues: number[] = [];
  const customData: HistogramTraceCustomData[] = [];

  bins.forEach((bin) => {
    const endExclusive = Math.min(bin.binEnd + 1, sourceBinCount);
    const buildPointData = (pointCount: number): HistogramTraceCustomData => [
      bin.binLabel,
      bin.binWidth,
      sourceBinCount,
      histogramFlavor.totalCount,
      bin.normalizedWeight,
      formatPixelCount(pointCount),
    ];

    if (!xValues.length || xValues[xValues.length - 1] !== bin.binStart || yValues[yValues.length - 1] !== 0) {
      xValues.push(bin.binStart);
      yValues.push(0);
      customData.push(buildPointData(0));
    }

    xValues.push(bin.binStart, endExclusive, endExclusive);
    yValues.push(bin.count, bin.count, 0);
    customData.push(buildPointData(bin.count), buildPointData(bin.count), buildPointData(0));
  });

  return {
    xValues,
    yValues,
    customData,
  };
};

const getHistogramVisibleRange = ({
  bins,
  sourceBinCount,
}: {
  bins: HistogramPreviewBin[];
  sourceBinCount: number;
}) => {
  if (!bins.length) {
    return [-0.5, Math.max(sourceBinCount - 0.5, 0.5)];
  }

  const sortedBins = [...bins].sort((leftBin, rightBin) => leftBin.binStart - rightBin.binStart);
  const totalWeight = sortedBins.reduce((sum, bin) => sum + (bin.normalizedWeight || bin.count), 0);

  if (totalWeight <= 0) {
    const startBin = sortedBins[0];
    const endBin = sortedBins[sortedBins.length - 1];
    return [Math.max(0, startBin.binStart) - 0.5, Math.min(sourceBinCount - 1, endBin.binEnd) + 0.5];
  }

  const lowerThreshold = totalWeight * histogramLowerMassCutoff;
  const upperThreshold = totalWeight * histogramUpperMassCutoff;

  let cumulativeWeight = 0;
  const lowerBoundBin =
    sortedBins.find((bin) => {
      cumulativeWeight += bin.normalizedWeight || bin.count;
      return cumulativeWeight >= lowerThreshold;
    }) || sortedBins[0];

  cumulativeWeight = 0;
  const upperBoundBin =
    sortedBins.find((bin) => {
      cumulativeWeight += bin.normalizedWeight || bin.count;
      return cumulativeWeight >= upperThreshold;
    }) || sortedBins[sortedBins.length - 1];

  const visibleStart = lowerBoundBin.binStart;
  const visibleEnd = upperBoundBin.binEnd;
  const span = Math.max(visibleEnd - visibleStart, 1);
  const padding = Math.max(2, Math.ceil(span * 0.08));

  return [Math.max(0, visibleStart - padding) - 0.5, Math.min(sourceBinCount - 1, visibleEnd + padding) + 0.5];
};

const getHistogramYAxisRange = ({
  bins,
  xRange,
}: {
  bins: HistogramPreviewBin[];
  xRange: number[];
}): [number, number] | undefined => {
  const [visibleStart, visibleEnd] = xRange;
  const visibleCounts = bins
    .filter((bin) => bin.binEnd >= visibleStart && bin.binStart <= visibleEnd)
    .map((bin) => bin.count)
    .filter((count) => count > 0);

  if (!visibleCounts.length) {
    return undefined;
  }

  return [0, Math.max(...visibleCounts) * 1.08];
};

const buildHistogramPreviewTrace = ({
  flavor,
  histogramFlavor,
  color,
  fillTrace,
}: {
  flavor: string;
  histogramFlavor: HistogramFlavorPreview;
  color: string;
  fillTrace: boolean;
}) => {
  const label = getHistogramFlavorLabel(flavor);
  const bins = buildHistogramPreviewBins(histogramFlavor);
  const { xValues, yValues, customData } = buildHistogramTracePoints({ bins, histogramFlavor });

  return {
    type: 'scatter',
    mode: 'lines',
    name: label,
    x: xValues,
    y: yValues,
    customdata: customData,
    line: {
      color,
      width: fillTrace ? 2.4 : 2,
    },
    fill: fillTrace ? 'tozeroy' : 'none',
    fillcolor: fillTrace ? color.replace('0.95', '0.22').replace('0.96', '0.22').replace('0.92', '0.18') : undefined,
    hoverlabel: {
      namelength: -1,
    },
    hovertemplate:
      '<b>' +
      label +
      '</b><br>signal=%{customdata[0]}<br>pixel count=%{customdata[5]}<br>fraction=%{customdata[4]:.6f}<br>bin width=%{customdata[1]}<br>source bins=%{customdata[2]}<extra></extra>',
  };
};

export const buildMultichannelRangeOverviewFigure = ({
  observations,
  getMarkerLabel,
}: {
  observations: MarkerNormalizationObservation[];
  getMarkerLabel: (markerId: string) => string;
}): PlotlyFigure => {
  const baseCustomData = observations.map((observation) => [
    observation.slideId,
    observation.markerId,
    getMarkerLabel(observation.markerId),
    observation.minValue,
    observation.maxValue,
    observation.dynamicRange,
    observation.rangeZScore,
    observation.isOutlier ? 'Outlier' : 'Within range',
  ]);
  const yValues = observations.map((observation) => getMarkerLabel(observation.markerId));

  return {
    data: [
      {
        type: 'box',
        orientation: 'h',
        name: 'Lower bound (min_c)',
        x: observations.map((observation) => observation.minValue),
        y: yValues,
        customdata: baseCustomData,
        boxpoints: 'all',
        jitter: 0.45,
        pointpos: 0,
        marker: { color: 'rgba(2, 136, 209, 0.45)', size: 4 },
        line: { color: 'rgba(2, 136, 209, 0.78)' },
        hovertemplate:
          '<b>%{customdata[0]}</b><br>marker=%{customdata[2]}<br>lower bound=%{x}<br>upper bound=%{customdata[4]}<br>dynamic range=%{customdata[5]}<br>range z-score=%{customdata[6]:.2f}<br>%{customdata[7]}<extra></extra>',
      },
      {
        type: 'box',
        orientation: 'h',
        name: 'Upper bound (max_c)',
        x: observations.map((observation) => observation.maxValue),
        y: yValues,
        customdata: baseCustomData,
        boxpoints: 'all',
        jitter: 0.45,
        pointpos: 0,
        marker: { color: 'rgba(56, 142, 60, 0.45)', size: 4 },
        line: { color: 'rgba(56, 142, 60, 0.78)' },
        hovertemplate:
          '<b>%{customdata[0]}</b><br>marker=%{customdata[2]}<br>lower bound=%{customdata[3]}<br>upper bound=%{x}<br>dynamic range=%{customdata[5]}<br>range z-score=%{customdata[6]:.2f}<br>%{customdata[7]}<extra></extra>',
      },
      {
        type: 'box',
        orientation: 'h',
        name: 'Dynamic range',
        x: observations.map((observation) => observation.dynamicRange),
        y: yValues,
        customdata: baseCustomData,
        boxpoints: 'all',
        jitter: 0.45,
        pointpos: 0,
        marker: { color: 'rgba(239, 108, 0, 0.45)', size: 4 },
        line: { color: 'rgba(239, 108, 0, 0.82)' },
        hovertemplate:
          '<b>%{customdata[0]}</b><br>marker=%{customdata[2]}<br>lower bound=%{customdata[3]}<br>upper bound=%{customdata[4]}<br>dynamic range=%{x}<br>range z-score=%{customdata[6]:.2f}<br>%{customdata[7]}<extra></extra>',
      },
    ],
    layout: {
      title: { text: 'Channel Dynamic Range — Multichannel Overview' },
      margin: { l: 120, r: 24, t: 56, b: 56 },
      meta: {
        themeMode: 'client',
        themeModifiers: buildPlotThemeModifiers('box'),
        pointLink: buildStudySlidePointLink(0, 1),
      },
      legend: { orientation: 'h', y: 1.02, yanchor: 'bottom', x: 0, xanchor: 'left' },
      boxmode: 'group',
      xaxis: { title: { text: 'Current normalization value' } },
      yaxis: { title: { text: 'Marker' }, automargin: true },
    },
  };
};

export const buildChannelHistogramPreviewFigure = ({
  histogramPreview,
  markerLabel,
  visibleFlavorNames,
}: {
  histogramPreview: ChannelHistogramPreview;
  markerLabel: string;
  visibleFlavorNames?: string[];
}): PlotlyFigure | null => {
  const allFlavorEntries = getHistogramFlavorEntries(histogramPreview);
  const visibleFlavorSet = visibleFlavorNames?.length ? new Set(visibleFlavorNames) : null;
  const flavorEntries = visibleFlavorSet
    ? allFlavorEntries.filter(([flavorName]) => visibleFlavorSet.has(flavorName))
    : allFlavorEntries;

  if (!flavorEntries.length) {
    return null;
  }

  const sourceBinCount = Math.max(...flavorEntries.map(([, flavorPreview]) => flavorPreview.sourceBinCount || 0), 0);
  const histogramBins = flavorEntries.flatMap(([, flavorPreview]) => buildHistogramPreviewBins(flavorPreview));
  const xRange = getHistogramVisibleRange({ bins: histogramBins, sourceBinCount });
  const traces = flavorEntries.map(([flavorName, flavorPreview], visibleFlavorIndex) =>
    buildHistogramPreviewTrace({
      flavor: flavorName,
      histogramFlavor: flavorPreview,
      color: getHistogramFlavorColor(
        flavorName,
        Math.max(
          allFlavorEntries.findIndex(([candidateFlavorName]) => candidateFlavorName === flavorName),
          visibleFlavorIndex
        )
      ),
      fillTrace: visibleFlavorIndex === 0,
    })
  ) as Array<Record<string, unknown>>;

  if (!traces.length) {
    return null;
  }

  return {
    data: traces,
    layout: {
      title: { text: `${markerLabel} — Signal Histogram` },
      margin: { l: 60, r: 24, t: 56, b: 56 },
      hovermode: 'x unified',
      showlegend: false,
      meta: {
        themeMode: 'client',
        themeModifiers: buildPlotThemeModifiers('scatter'),
      },
      paper_bgcolor: 'rgba(0, 0, 0, 0)',
      plot_bgcolor: 'rgba(15, 23, 42, 0.02)',
      xaxis: {
        title: { text: getHistogramXAxisTitle(sourceBinCount, histogramPreview.bitDepth) },
        tickformat: ',d',
        range: xRange,
        showgrid: false,
        zeroline: false,
      },
      yaxis: {
        title: { text: 'Count' },
        range: getHistogramYAxisRange({ bins: histogramBins, xRange }),
        rangemode: 'tozero',
        tickformat: ',d',
        gridcolor: 'rgba(15, 23, 42, 0.12)',
        zeroline: false,
      },
      legend: { orientation: 'h', y: 1.02, yanchor: 'bottom', x: 0, xanchor: 'left' },
    },
  };
};

export const buildDynamicRangeBoxPlotFigure = ({
  observations,
  markerLabel,
}: {
  observations: MarkerNormalizationObservation[];
  markerLabel: string;
}): PlotlyFigure => {
  const pointColors = observations.map((observation) =>
    observation.isOutlier ? 'rgba(211, 47, 47, 0.88)' : 'rgba(25, 118, 210, 0.72)'
  );
  const hoverData = observations.map((observation) => [
    observation.slideId,
    observation.minValue,
    observation.maxValue,
    observation.dynamicRange,
    observation.rangeZScore,
    observation.isOutlier ? 'Outlier' : 'Within range',
  ]);

  return {
    data: [
      {
        type: 'box',
        name: markerLabel,
        y: observations.map((observation) => observation.dynamicRange),
        marker: { color: 'rgba(25, 118, 210, 0.18)' },
        line: { color: 'rgba(25, 118, 210, 0.7)' },
        boxpoints: false,
        hoverinfo: 'skip',
      },
      {
        type: 'scatter',
        mode: 'markers',
        name: 'Slides',
        x: observations.map(() => markerLabel),
        y: observations.map((observation) => observation.dynamicRange),
        customdata: hoverData,
        marker: {
          size: 10,
          color: pointColors,
          line: { color: 'rgba(255,255,255,0.9)', width: 1 },
        },
        hovertemplate:
          '<b>%{customdata[0]}</b><br>min_c=%{customdata[1]}<br>max_c=%{customdata[2]}<br>dynamic range=%{customdata[3]}<br>range z-score=%{customdata[4]:.2f}<br>%{customdata[5]}<extra></extra>',
      },
    ],
    layout: {
      title: { text: `${markerLabel} — Dynamic Range Box Plot` },
      showlegend: false,
      margin: { l: 60, r: 20, t: 56, b: 56 },
      meta: {
        themeMode: 'client',
        themeModifiers: buildPlotThemeModifiers('box'),
        pointLink: buildStudySlidePointLink(0),
      },
      xaxis: { title: { text: 'Marker' } },
      yaxis: { title: { text: 'Dynamic range (max_c - min_c)' } },
    },
  };
};

export const buildMarkerZScoreScatterFigure = ({
  observations,
  markerLabel,
}: {
  observations: MarkerNormalizationObservation[];
  markerLabel: string;
}): PlotlyFigure => {
  const maxAbsoluteZScore = Math.max(...observations.map((observation) => observation.absoluteMaxZScore), 0);
  const axisLimit = getAxisLimit(maxAbsoluteZScore);
  const pointColors = observations.map((observation) =>
    observation.isOutlier ? 'rgba(211, 47, 47, 0.88)' : 'rgba(46, 125, 50, 0.78)'
  );

  return {
    data: [
      {
        type: 'scatter',
        mode: 'markers',
        name: markerLabel,
        x: observations.map((observation) => observation.minZScore),
        y: observations.map((observation) => observation.maxZScore),
        customdata: observations.map((observation) => [
          observation.slideId,
          observation.minValue,
          observation.maxValue,
          observation.dynamicRange,
          observation.rangeZScore,
          observation.isOutlier ? 'Outlier' : 'Within range',
        ]),
        marker: {
          size: 11,
          color: pointColors,
          line: { color: 'rgba(255,255,255,0.9)', width: 1 },
        },
        hovertemplate:
          '<b>%{customdata[0]}</b><br>min_c=%{customdata[1]} (z=%{x:.2f})<br>max_c=%{customdata[2]} (z=%{y:.2f})<br>dynamic range=%{customdata[3]}<br>range z-score=%{customdata[4]:.2f}<br>%{customdata[5]}<extra></extra>',
      },
    ],
    layout: {
      title: { text: `${markerLabel} — Z-Score Scatter` },
      showlegend: false,
      margin: { l: 60, r: 20, t: 56, b: 56 },
      meta: {
        themeMode: 'client',
        themeModifiers: buildPlotThemeModifiers('scatter'),
        pointLink: buildStudySlidePointLink(0),
      },
      xaxis: {
        title: { text: 'Min z-score' },
        range: [-axisLimit, axisLimit],
      },
      yaxis: {
        title: { text: 'Max z-score' },
        range: [-axisLimit, axisLimit],
      },
      shapes: [
        {
          type: 'line',
          x0: 0,
          x1: 0,
          y0: -axisLimit,
          y1: axisLimit,
          line: { color: 'rgba(120, 120, 120, 0.55)', width: 1 },
        },
        {
          type: 'line',
          x0: -axisLimit,
          x1: axisLimit,
          y0: 0,
          y1: 0,
          line: { color: 'rgba(120, 120, 120, 0.55)', width: 1 },
        },
        {
          type: 'line',
          x0: 2,
          x1: 2,
          y0: -axisLimit,
          y1: axisLimit,
          line: {
            color: 'rgba(211, 47, 47, 0.35)',
            width: 1,
            dash: 'dot',
          },
        },
        {
          type: 'line',
          x0: -2,
          x1: -2,
          y0: -axisLimit,
          y1: axisLimit,
          line: {
            color: 'rgba(211, 47, 47, 0.35)',
            width: 1,
            dash: 'dot',
          },
        },
        {
          type: 'line',
          x0: -axisLimit,
          x1: axisLimit,
          y0: 2,
          y1: 2,
          line: {
            color: 'rgba(211, 47, 47, 0.35)',
            width: 1,
            dash: 'dot',
          },
        },
        {
          type: 'line',
          x0: -axisLimit,
          x1: axisLimit,
          y0: -2,
          y1: -2,
          line: {
            color: 'rgba(211, 47, 47, 0.35)',
            width: 1,
            dash: 'dot',
          },
        },
      ],
    },
  };
};

export const buildMultichannelZScoreScatterFigure = ({
  slideSummaries,
  getMarkerLabel,
}: {
  slideSummaries: SlideNormalizationSummary[];
  getMarkerLabel: (markerId: string) => string;
}): PlotlyFigure => {
  const maxAbsoluteZScore = Math.max(...slideSummaries.map((slideSummary) => slideSummary.maxAbsoluteZScore), 0);
  const axisLimit = getAxisLimit(maxAbsoluteZScore);

  return {
    data: [
      {
        type: 'scatter',
        mode: 'markers',
        name: 'Slides',
        x: slideSummaries.map((slideSummary) => slideSummary.dominantMinZScore),
        y: slideSummaries.map((slideSummary) => slideSummary.dominantMaxZScore),
        customdata: slideSummaries.map((slideSummary) => [
          slideSummary.slideId,
          getMarkerLabel(slideSummary.dominantMinMarkerId),
          slideSummary.dominantMinZScore,
          getMarkerLabel(slideSummary.dominantMaxMarkerId),
          slideSummary.dominantMaxZScore,
          getMarkerLabel(slideSummary.dominantRangeMarkerId),
          slideSummary.dominantRangeZScore,
          slideSummary.outlierMarkerCount,
          slideSummary.markerCount,
          slideSummary.maxAbsoluteZScore,
        ]),
        marker: {
          size: slideSummaries.map((slideSummary) => getBubbleSize(slideSummary.dominantRangeZScore)),
          color: slideSummaries.map((slideSummary) => slideSummary.maxAbsoluteZScore),
          colorscale: 'YlOrRd',
          line: { color: 'rgba(255,255,255,0.9)', width: 1 },
          colorbar: { title: 'Max |z|' },
          opacity: 0.86,
        },
        hovertemplate:
          '<b>%{customdata[0]}</b><br>dominant low drift=%{customdata[1]} (%{customdata[2]:.2f})<br>dominant high drift=%{customdata[3]} (%{customdata[4]:.2f})<br>dominant range drift=%{customdata[5]} (%{customdata[6]:.2f})<br>outlier markers=%{customdata[7]} / %{customdata[8]}<br>max |z|=%{customdata[9]:.2f}<extra></extra>',
      },
    ],
    layout: {
      title: { text: 'Channel Dynamic Range — Z-Score Outlier Map' },
      showlegend: false,
      margin: { l: 60, r: 24, t: 56, b: 56 },
      meta: {
        themeMode: 'client',
        themeModifiers: buildPlotThemeModifiers('scatter'),
        pointLink: buildStudySlidePointLink(0),
      },
      xaxis: {
        title: { text: 'Dominant lower-bound z-score' },
        range: [-axisLimit, axisLimit],
      },
      yaxis: {
        title: { text: 'Dominant upper-bound z-score' },
        range: [-axisLimit, axisLimit],
      },
      shapes: [
        {
          type: 'line',
          x0: 0,
          x1: 0,
          y0: -axisLimit,
          y1: axisLimit,
          line: { color: 'rgba(120, 120, 120, 0.55)', width: 1 },
        },
        {
          type: 'line',
          x0: -axisLimit,
          x1: axisLimit,
          y0: 0,
          y1: 0,
          line: { color: 'rgba(120, 120, 120, 0.55)', width: 1 },
        },
        {
          type: 'line',
          x0: 2,
          x1: 2,
          y0: -axisLimit,
          y1: axisLimit,
          line: {
            color: 'rgba(211, 47, 47, 0.35)',
            width: 1,
            dash: 'dot',
          },
        },
        {
          type: 'line',
          x0: -2,
          x1: -2,
          y0: -axisLimit,
          y1: axisLimit,
          line: {
            color: 'rgba(211, 47, 47, 0.35)',
            width: 1,
            dash: 'dot',
          },
        },
        {
          type: 'line',
          x0: -axisLimit,
          x1: axisLimit,
          y0: 2,
          y1: 2,
          line: {
            color: 'rgba(211, 47, 47, 0.35)',
            width: 1,
            dash: 'dot',
          },
        },
        {
          type: 'line',
          x0: -axisLimit,
          x1: axisLimit,
          y0: -2,
          y1: -2,
          line: {
            color: 'rgba(211, 47, 47, 0.35)',
            width: 1,
            dash: 'dot',
          },
        },
      ],
    },
  };
};

export const buildMarkerNormalizationFigureSets = ({
  observationsByMarkerId,
  orderedMarkerIds,
  getMarkerLabel,
}: {
  observationsByMarkerId: Record<string, MarkerNormalizationObservation[]>;
  orderedMarkerIds: string[];
  getMarkerLabel: (markerId: string) => string;
}): Record<string, MarkerNormalizationFigureSet> => {
  return orderedMarkerIds.reduce<Record<string, MarkerNormalizationFigureSet>>((accumulator, markerId) => {
    const observations = observationsByMarkerId[markerId] || [];
    const markerLabel = getMarkerLabel(markerId);

    accumulator[markerId] = {
      rangeFigure: observations.length
        ? buildDynamicRangeBoxPlotFigure({
            observations,
            markerLabel,
          })
        : null,
      zScoreFigure: observations.length
        ? buildMarkerZScoreScatterFigure({
            observations,
            markerLabel,
          })
        : null,
    };

    return accumulator;
  }, {});
};
